import { SHEPHERD_STAGES, type LeadStage, type NextAction, type ShepherdStage } from "./leadQuality";

/**
 * V1 STAGES (Agent Brains 5.4 to 5.8 Shepherd, B3, section 7). The pure half of
 * the stage ledger.
 *
 * `deriveStage` (leadQuality.ts) stays the ONLY stage logic. This file never
 * reads a record: it takes the stage `deriveStage` named and decides whether
 * that is a change worth an event, which next action the stage owes, and how a
 * cached projection is rebuilt from the events alone.
 *
 * NO WEALTH, NO OFFER (F-11, section 0.5). Nothing here reads a VIP field, a
 * price or an amount, and no line below names an offer.
 */

/** The owner-recorded delivery event: the one evidence source of Delivered. */
export const ORDER_DELIVERED_EVENT = "order.delivered";

/** The stage values a projection row can hold. */
export type RowStage = LeadStage;

export type DerivedBy = "rule" | "owner";

/** One `lead_stage_current` row, as the pure core sees it. */
export interface StageRow {
  readonly stage: RowStage;
  readonly enteredAt: Date;
  readonly eventId: string;
  readonly owner: string;
  readonly nextAction: string;
  readonly nextDueAt: Date | null;
  /** Read off the event the row points at: an owner-set stage holds against "not derivable". */
  readonly derivedBy: DerivedBy;
}

/**
 * `stage.changed` carries the situation reducer's stage names (EVENT_STAGES in
 * eventCatalog.ts), which have no "delivered" and call the pool "reactivation".
 * The pool is `reactivation`. Delivered is `won`: a delivered order is the won
 * state, and this file is the only writer of `stage.changed`, so the inverse
 * below is exact. (TA line in the V1 report: Kabir may prefer a seventh event
 * stage to a shared name.)
 */
export const EVENT_STAGE_OF: Readonly<Record<ShepherdStage, string>> = {
  new: "new",
  engaged: "engaged",
  consult_booked: "consult_booked",
  deposit: "deposit",
  delivered: "won",
  reactivation_pool: "reactivation",
};

/**
 * The close of a lead the record reads as lost and not written to since. `lost` is already in
 * EVENT_STAGES, so the close is a `stage.changed` to it (no new kind): the situation reducer reads it as
 * an explicit stage, and the projection holds no open row for the thread.
 */
export const LOST_EVENT_STAGE = "lost";

export const SHEPHERD_STAGE_OF: Readonly<Record<string, ShepherdStage>> = Object.freeze(
  Object.fromEntries(SHEPHERD_STAGES.map((s) => [EVENT_STAGE_OF[s], s])),
);

export interface StageReadingInput {
  readonly stage: LeadStage;
  /** Record ids behind the reading, ids only. */
  readonly cites: readonly string[];
  /** A code for a not-derivable reading. */
  readonly reason: string;
}

export type StageChange =
  | {
      readonly kind: "changed";
      readonly from: RowStage | null;
      readonly to: ShepherdStage;
      readonly derivedBy: DerivedBy;
      readonly cites: readonly string[];
    }
  | { readonly kind: "not_derivable"; readonly from: RowStage | null; readonly reason: string };

export type StageDecision =
  | { readonly action: "enter"; readonly change: StageChange }
  | { readonly action: "none"; readonly reason: "unchanged" | "owner_hold" | "backward_refused" };

const RANK: Readonly<Record<ShepherdStage, number>> = {
  new: 0,
  engaged: 1,
  consult_booked: 2,
  deposit: 3,
  delivered: 4,
  reactivation_pool: 5,
};

/**
 * Backward moves that are real. A customer writes again after the shop's
 * reply (engaged to new: the clock restarts each exchange, STAGE_SLA_MS), and a
 * customer who was delivered to or ghosted comes back (any earlier stage). Any
 * other backward move (a deposit that reads as a consult again) is refused and
 * counted, never written. Not derivable is not a rank: it is reachable from and
 * to every stage, which is how returned-after-lost arrives.
 * AWAITING RATIFICATION: whether a cancelled consult or a reversed deposit
 * should walk a lead back is Kabir's; today the move is refused and counted.
 */
function backwardAllowed(from: ShepherdStage, to: ShepherdStage): boolean {
  if (from === "engaged" && to === "new") return true;
  if (from === "delivered") return to === "new" || to === "engaged" || to === "consult_booked" || to === "deposit";
  // A pool customer who buys ready-made, with the sale and the delivery in one watch run, goes to delivered.
  if (from === "reactivation_pool") return to === "new" || to === "engaged" || to === "consult_booked" || to === "deposit" || to === "delivered";
  return false;
}

/**
 * Whether one derived reading is a change worth an event. `prev` is the cached
 * row (null for a lead never seen), `reading` is what `deriveStage` said now.
 */
export function stageTransition(prev: StageRow | null, reading: StageReadingInput, _now: Date): StageDecision {
  if (reading.stage === "not_derivable") {
    if (prev !== null && prev.stage === "not_derivable") return { action: "none", reason: "unchanged" };
    // The owner placed this lead because the record could not; the record still cannot.
    // The hold ends only when the record names a stage: that reading enters if it ranks above the owner's
    // stage (or is an allowed backward move), is unchanged if equal, and is refused and counted if it
    // ranks below. It does not yield to every reading.
    if (prev !== null && prev.derivedBy === "owner") return { action: "none", reason: "owner_hold" };
    return { action: "enter", change: { kind: "not_derivable", from: prev?.stage ?? null, reason: reading.reason } };
  }
  const to = reading.stage;
  const enter: StageDecision = {
    action: "enter",
    change: { kind: "changed", from: prev?.stage ?? null, to, derivedBy: "rule", cites: [...reading.cites] },
  };
  if (prev === null || prev.stage === "not_derivable") return enter;
  if (prev.stage === to) return { action: "none", reason: "unchanged" };
  if (RANK[to] > RANK[prev.stage]) return enter;
  return backwardAllowed(prev.stage, to) ? enter : { action: "none", reason: "backward_refused" };
}

/** The code a not-derivable reading is filed under. Codes only, never the record's words. */
export function notDerivableReason(reading: {
  readonly returnedAfterLost?: boolean;
  readonly evidence: readonly { readonly source: string; readonly note: string }[];
}): string {
  if (reading.returnedAfterLost === true) return "returned_after_lost";
  const first = reading.evidence[0];
  if (first === undefined) return "no_customer_message";
  if (first.source === "outcomes.sold") return "sold_not_delivered";
  if (first.source === "outcomes.ghosted") {
    const second = reading.evidence[1];
    return second !== undefined && second.note.includes("after the ghosted") ? "ghosted_then_wrote" : "ghosted_unanswered";
  }
  return "unplaceable";
}

// ---- dated next actions -----------------------------------------------------

export type DatedActionCode =
  | "respond"
  | "qualify"
  | "hold_consult"
  | "record_delivery"
  | "review_ask"
  | "reactivation_touch"
  | "parked"
  | "set_stage"
  | "advance";

export interface DatedAction {
  readonly action: DatedActionCode;
  readonly dueAt: Date | null;
  /** Counts as dated: it has a due time, or it is a parked pool lead (not a bug). */
  readonly dated: boolean;
  /** False only for a lead the owner must place first; that lead is counted as not derivable instead. */
  readonly countsAsUndated: boolean;
  readonly slaMs: number | null;
  readonly note: string;
}

/**
 * Extras the record alone cannot give. Each is injected and optional; an absent
 * or invalid one reads as no date, which the report counts.
 */
export interface NextActionExtras {
  /** consult_booked: the appointment's `scheduled_at`. */
  readonly consultAt?: Date | null;
  /** deposit: an owner-set delivery date. No V1 writer sets it yet (TA line). */
  readonly deliveryDueAt?: Date | null;
  /** delivered: when V4 says the review ask becomes eligible. Injected, never imported. */
  readonly reviewEligibleAt?: Date | null;
  /** reactivation_pool: the next planned touch from V2. Injected, never imported. */
  readonly nextTouchAt?: Date | null;
}

const valid = (d: Date | null | undefined): Date | null => (d instanceof Date && !Number.isNaN(d.getTime()) ? d : null);

/**
 * One owner and one dated next action per lead. New and Engaged keep the SLA
 * clock `nextActionFor` gives them. The other stages had none; the four below
 * are the V1 design's, each AWAITING RATIFICATION (design open question 2):
 * consult_booked `hold_consult` at the appointment time, deposit
 * `record_delivery` on an owner-set date, delivered `review_ask` at V4's
 * eligible time, reactivation_pool the next planned touch or `parked`.
 * `base` is the NextAction `assessLead` already computed for the stage.
 */
export function datedNextAction(stage: LeadStage, base: NextAction, extras: NextActionExtras): DatedAction {
  const make = (action: DatedActionCode, dueAt: Date | null, note: string, over: Partial<DatedAction> = {}): DatedAction => ({
    action,
    dueAt,
    dated: dueAt !== null,
    countsAsUndated: true,
    slaMs: null,
    note,
    ...over,
  });
  switch (stage) {
    case "new":
    case "engaged":
      return make(base.action === "respond" ? "respond" : base.action === "qualify" ? "qualify" : "advance", base.dueAt, base.note, {
        slaMs: base.slaMs,
      });
    case "consult_booked":
      return make("hold_consult", valid(extras.consultAt), "hold the consult at the appointment time");
    case "deposit":
      return make("record_delivery", valid(extras.deliveryDueAt), "record the delivery on the owner-set date");
    case "delivered":
      return make("review_ask", valid(extras.reviewEligibleAt), "the review ask is due when it becomes eligible");
    case "reactivation_pool": {
      const touch = valid(extras.nextTouchAt);
      return touch !== null
        ? make("reactivation_touch", touch, "the next planned touch")
        : make("parked", null, "parked: no touch is planned", { dated: true });
    }
    case "not_derivable":
      return make("set_stage", null, "stage not derivable: the owner sets the stage", { dated: false, countsAsUndated: false });
  }
}

// ---- the projection, rebuilt from events --------------------------------------

export interface StageEventRow {
  readonly id: string;
  readonly type: string;
  readonly threadId: string | null;
  readonly occurredAt: Date;
  readonly payload: unknown;
}

export interface RebuiltStageRow {
  readonly stage: RowStage;
  readonly enteredAt: Date;
  readonly eventId: string;
  readonly owner: string;
  readonly derivedBy: DerivedBy;
}

const asObj = (p: unknown): Record<string, unknown> => (typeof p === "object" && p !== null ? (p as Record<string, unknown>) : {});
const byTimeThenId = (a: StageEventRow, b: StageEventRow): number =>
  a.occurredAt.getTime() - b.occurredAt.getTime() || a.id.localeCompare(b.id);

/** The shop owner, the column default of `lead_stage_current.owner`. */
export const DEFAULT_LEAD_OWNER = "owner";

/**
 * The projection's stage, entry time, entry event, owner and derivation, from
 * the events alone: the latest `stage.changed` or `stage.not_derivable` per
 * thread, and the latest `lead.owner_assigned`. The next action and its due
 * time are NOT here: they follow the clock and the record, are recomputed on
 * every watch, and carry no event (TA line in the V1 report).
 * A `stage.changed` naming no known stage is dropped, as the situation reducer
 * drops it.
 */
export function rebuildStageRows(events: readonly StageEventRow[]): Map<string, RebuiltStageRow> {
  const stages = new Map<string, Omit<RebuiltStageRow, "owner">>();
  const owners = new Map<string, string>();
  // A lost close (`stage.changed` to "lost", written when the record reads a lead as closed) ends the row:
  // the lead has no open stage and the owner assignment goes with it, so a customer who writes again starts fresh.
  for (const e of [...events].sort(byTimeThenId)) {
    if (e.threadId === null) continue;
    const p = asObj(e.payload);
    if (e.type === "stage.changed") {
      const name = p["to"];
      if (typeof name !== "string") continue;
      if (name === LOST_EVENT_STAGE) {
        stages.delete(e.threadId);
        owners.delete(e.threadId);
        continue;
      }
      if (!Object.hasOwn(SHEPHERD_STAGE_OF, name)) continue;
      const to = SHEPHERD_STAGE_OF[name] as ShepherdStage;
      stages.set(e.threadId, { stage: to, enteredAt: e.occurredAt, eventId: e.id, derivedBy: p["derivedBy"] === "owner" ? "owner" : "rule" });
    } else if (e.type === "stage.not_derivable") {
      stages.set(e.threadId, { stage: "not_derivable", enteredAt: e.occurredAt, eventId: e.id, derivedBy: "rule" });
    } else if (e.type === "lead.owner_assigned" && typeof p["owner"] === "string") {
      owners.set(e.threadId, p["owner"]);
    }
  }
  const out = new Map<string, RebuiltStageRow>();
  for (const [threadId, s] of [...stages].sort(([a], [b]) => a.localeCompare(b))) {
    out.set(threadId, { ...s, owner: owners.get(threadId) ?? DEFAULT_LEAD_OWNER });
  }
  return out;
}
